/**
 * Move generation utilities
 */

import type { MoveType } from "../types/actions";
import type { MovementPattern } from "../types/augmentation";
import type { Color, Square } from "../types/common";
import type { ChessState } from "../types/game-state";
import type { Piece } from "../types/piece";
import {
  calculateCaptureChance,
  checkConsecutiveCheckWin,
  getAugmentationsForPiece,
  getEffectiveMovementType,
  getPromotionRankIndices,
  hasAssassinAug,
  hasKingFury,
  hasLastPawnMovement,
  hasPhaseMovement,
  hasReverseMovement,
  hasRookLineCapture,
  isKingAdjacentImmune,
  isKnightWeakened,
  isLastPawn,
  isShieldWallImmune,
  isUnmovedImmune,
} from "./augmentation-effects";
import { getKing, getOppositeColor, getPieceAt } from "./board";
import { indicesToSquare, isInBounds, squareToIndices } from "./square";

/** Direction vector for piece movement */
interface Direction {
  fileOffset: number;
  rankOffset: number;
}

/** Rook movement directions (orthogonal) */
const ROOK_DIRECTIONS: Direction[] = [
  { fileOffset: 0, rankOffset: 1 }, // up
  { fileOffset: 0, rankOffset: -1 }, // down
  { fileOffset: 1, rankOffset: 0 }, // right
  { fileOffset: -1, rankOffset: 0 }, // left
];

/** Bishop movement directions (diagonal) */
const BISHOP_DIRECTIONS: Direction[] = [
  { fileOffset: 1, rankOffset: 1 }, // up-right
  { fileOffset: 1, rankOffset: -1 }, // down-right
  { fileOffset: -1, rankOffset: 1 }, // up-left
  { fileOffset: -1, rankOffset: -1 }, // down-left
];

/** Queen/King movement directions (all 8 directions) */
const ALL_DIRECTIONS: Direction[] = [...ROOK_DIRECTIONS, ...BISHOP_DIRECTIONS];

/** Knight movement offsets */
const KNIGHT_OFFSETS: Direction[] = [
  { fileOffset: 1, rankOffset: 2 },
  { fileOffset: 2, rankOffset: 1 },
  { fileOffset: 2, rankOffset: -1 },
  { fileOffset: 1, rankOffset: -2 },
  { fileOffset: -1, rankOffset: -2 },
  { fileOffset: -2, rankOffset: -1 },
  { fileOffset: -2, rankOffset: 1 },
  { fileOffset: -1, rankOffset: 2 },
];

/**
 * Generate moves for a specific movement pattern
 */
function generatePatternMoves(
  state: ChessState,
  piece: Piece,
  pattern: MovementPattern,
): PossibleMove[] {
  const moves: PossibleMove[] = [];
  const { fileIndex, rankIndex } = squareToIndices(piece.position);

  let offsets: Direction[] = [];

  switch (pattern) {
    case "KING_STEP":
      // 1 square in any direction
      offsets = ALL_DIRECTIONS;
      break;

    case "DIAGONAL_2":
      // 2 squares diagonally (exactly 2, not sliding)
      offsets = BISHOP_DIRECTIONS.map((d) => ({
        fileOffset: d.fileOffset * 2,
        rankOffset: d.rankOffset * 2,
      }));
      break;

    case "ORTHOGONAL_2":
      // 2 squares orthogonally (exactly 2, not sliding)
      offsets = ROOK_DIRECTIONS.map((d) => ({
        fileOffset: d.fileOffset * 2,
        rankOffset: d.rankOffset * 2,
      }));
      break;

    case "FORWARD_2":
      // 2 squares forward (for pawns from any position)
      {
        const direction = piece.color === "WHITE" ? 1 : -1;
        offsets = [{ fileOffset: 0, rankOffset: direction * 2 }];
      }
      break;

    case "FORWARD_3":
      // 3 squares forward (for pawns from starting rank only; path check done in loop)
      {
        const direction = piece.color === "WHITE" ? 1 : -1;
        offsets = [{ fileOffset: 0, rankOffset: direction * 3 }];
      }
      break;

    case "FORWARD_CAPTURE_1":
      // Capture-only: 1 square directly forward (enemy must be present)
      {
        const direction = piece.color === "WHITE" ? 1 : -1;
        offsets = [{ fileOffset: 0, rankOffset: direction }];
      }
      break;

    case "BACKWARD_1":
      // Move-only: 1 square directly backward (no capture)
      {
        const direction = piece.color === "WHITE" ? -1 : 1;
        offsets = [{ fileOffset: 0, rankOffset: direction }];
      }
      break;
  }

  for (const offset of offsets) {
    const fi = fileIndex + offset.fileOffset;
    const ri = rankIndex + offset.rankOffset;

    if (!isInBounds(fi, ri)) continue;

    const targetSquare = indicesToSquare(fi, ri);
    if (!targetSquare) continue;

    const targetPiece = getPieceAt(state, targetSquare);

    // For FORWARD_2 pattern (pawn moves), check that path is clear
    if (pattern === "FORWARD_2") {
      const direction = piece.color === "WHITE" ? 1 : -1;
      const intermediateRi = rankIndex + direction;
      const intermediateSquare = indicesToSquare(fileIndex, intermediateRi);
      if (intermediateSquare && getPieceAt(state, intermediateSquare)) {
        continue; // Path is blocked
      }
      // Pawns don't capture forward
      if (targetPiece) continue;

      moves.push({
        from: piece.position,
        to: targetSquare,
        isCapture: false,
      });
    } else if (pattern === "FORWARD_3") {
      // Only available from starting rank (pawn's unmoved position)
      const startRankIndex = piece.color === "WHITE" ? 1 : 6; // 0-indexed
      if (rankIndex !== startRankIndex) continue;

      const direction = piece.color === "WHITE" ? 1 : -1;
      // Check the full path is clear at +1, +2, and +3 squares forward
      const sq1 = indicesToSquare(fileIndex, rankIndex + direction);
      const sq2 = indicesToSquare(fileIndex, rankIndex + direction * 2);
      if (
        (sq1 && getPieceAt(state, sq1)) ||
        (sq2 && getPieceAt(state, sq2)) ||
        targetPiece
      )
        continue;

      moves.push({
        from: piece.position,
        to: targetSquare,
        isCapture: false,
      });
    } else if (pattern === "FORWARD_CAPTURE_1") {
      // Capture-only forward: enemy must be present at target square
      if (!targetPiece) continue;
      if (targetPiece.color === piece.color) continue;
      const promotionRankIndices = getPromotionRankIndices(state, piece);
      const isPromotion = promotionRankIndices.includes(rankIndex);
      moves.push({
        from: piece.position,
        to: targetSquare,
        isCapture: true,
        capturedPieceId: targetPiece.id,
        moveType: isPromotion ? "PROMOTION" : "CAPTURE",
        isPromotion,
      });
    } else if (pattern === "BACKWARD_1") {
      // Move-only backward: target must be empty (no capture)
      if (targetPiece) continue;
      moves.push({
        from: piece.position,
        to: targetSquare,
        isCapture: false,
      });
    } else {
      // For other patterns, standard move/capture rules
      if (targetPiece === null) {
        moves.push({
          from: piece.position,
          to: targetSquare,
          isCapture: false,
        });
      } else if (targetPiece.color !== piece.color) {
        moves.push({
          from: piece.position,
          to: targetSquare,
          isCapture: true,
          capturedPieceId: targetPiece.id,
        });
      }
    }
  }

  return moves;
}

/**
 * Apply augmentation effects to generate additional moves
 */
function applyAugmentationEffects(
  state: ChessState,
  piece: Piece,
  baseMoves: PossibleMove[],
): PossibleMove[] {
  const augmentations = getAugmentationsForPiece(state, piece);

  if (augmentations.length === 0) {
    return baseMoves;
  }

  const additionalMoves: PossibleMove[] = [];

  for (const aug of augmentations) {
    if (aug.effect.type === "ADD_PATTERN" && aug.effect.pattern) {
      const patternMoves = generatePatternMoves(
        state,
        piece,
        aug.effect.pattern,
      );
      additionalMoves.push(...patternMoves);
    } else if (
      aug.effect.type === "REVERSE_MOVEMENT" &&
      piece.type === "PAWN"
    ) {
      // REVERSE_MOVEMENT on PAWN: add backward 1-square moves
      const backwardMoves = generatePatternMoves(state, piece, "BACKWARD_1");
      additionalMoves.push(...backwardMoves);
    }
    // REVERSE_MOVEMENT on QUEEN: range limit is handled in generatePseudoLegalMoves
  }

  // Merge and deduplicate moves
  const allMoves = [...baseMoves, ...additionalMoves];
  const uniqueMoves = new Map<string, PossibleMove>();

  for (const move of allMoves) {
    const key = `${move.to.file}${move.to.rank}`;
    // Keep the first occurrence (base moves take priority for move type)
    if (!uniqueMoves.has(key)) {
      uniqueMoves.set(key, move);
    }
  }

  return Array.from(uniqueMoves.values());
}

/** Possible move result */
export interface PossibleMove {
  from: Square;
  to: Square;
  isCapture: boolean;
  capturedPieceId?: string;
  /** Type of special move */
  moveType?: MoveType;
  /** For en passant: position of captured pawn */
  enPassantCaptureSquare?: Square;
  /** For promotion: indicates this move requires promotion choice */
  isPromotion?: boolean;
  /** For Domination Rook (RK-005): ID of piece passed through and captured en route */
  capturedEnRoutePieceId?: string;
  /** For Domination Rook (RK-005): board square of the en-route captured piece */
  capturedEnRouteSquare?: Square;
  /** For KN-004 Assassin: weakened knight captures non-queen — both pieces removed */
  isMutualDestruction?: boolean;
  /** Capture success probability (0.0–1.0). Absent = 1.0 (deterministic). */
  captureChance?: number;
}

/**
 * Generate moves for sliding pieces (Rook, Bishop, Queen)
 * Slides in each direction until blocked.
 * When phase=true, passes through all pieces (friendly and enemy).
 * When maxDistance is set, stops after that many squares.
 * When dominationRook=true, can pass through the first enemy piece (capturing it en route)
 * and reach any empty square or a second enemy piece beyond it.
 */
function generateSlidingMoves(
  state: ChessState,
  piece: Piece,
  directions: Direction[],
  options?: { phase?: boolean; maxDistance?: number; dominationRook?: boolean },
): PossibleMove[] {
  const moves: PossibleMove[] = [];
  const { fileIndex, rankIndex } = squareToIndices(piece.position);
  const phase = options?.phase ?? false;
  const maxDistance = options?.maxDistance ?? Number.POSITIVE_INFINITY;
  const dominationRook = options?.dominationRook ?? false;

  for (const dir of directions) {
    let fi = fileIndex + dir.fileOffset;
    let ri = rankIndex + dir.rankOffset;
    let dist = 1;
    // Track first enemy passed through (Domination Rook only)
    let enRoutePiece: { id: string; square: Square } | null = null;

    while (isInBounds(fi, ri) && dist <= maxDistance) {
      const targetSquare = indicesToSquare(fi, ri);
      if (!targetSquare) break;

      const targetPiece = getPieceAt(state, targetSquare);

      if (targetPiece === null) {
        if (enRoutePiece) {
          // Past first enemy (Domination Rook) — land here, capture en-route piece
          moves.push({
            from: piece.position,
            to: targetSquare,
            isCapture: true,
            capturedEnRoutePieceId: enRoutePiece.id,
            capturedEnRouteSquare: enRoutePiece.square,
          });
        } else {
          // Normal empty square move
          moves.push({
            from: piece.position,
            to: targetSquare,
            isCapture: false,
          });
        }
      } else if (targetPiece.color !== piece.color) {
        // Enemy piece
        if (enRoutePiece) {
          // Second enemy (Domination Rook) — land here, capture both
          if (!phase || targetPiece.type !== "KING") {
            moves.push({
              from: piece.position,
              to: targetSquare,
              isCapture: true,
              capturedPieceId: targetPiece.id,
              capturedEnRoutePieceId: enRoutePiece.id,
              capturedEnRouteSquare: enRoutePiece.square,
            });
          }
          break; // Stop after second enemy
        } else {
          // First enemy — standard capture
          if (!phase || targetPiece.type !== "KING") {
            moves.push({
              from: piece.position,
              to: targetSquare,
              isCapture: true,
              capturedPieceId: targetPiece.id,
            });
          }
          if (dominationRook && !phase) {
            // Continue past first enemy; track it as en-route
            enRoutePiece = { id: targetPiece.id, square: targetSquare };
          } else if (!phase) {
            break; // Normal: stop after first enemy capture
          }
          // Phase mode: continue sliding past enemies (enRoutePiece stays null)
        }
      } else {
        // Own piece — always block (even in Domination Rook mode)
        if (!phase) break;
        // Phase: skip own pieces
      }

      fi += dir.fileOffset;
      ri += dir.rankOffset;
      dist++;
    }
  }

  return moves;
}

/**
 * Generate moves for stepping pieces (King, Knight)
 * Moves exactly one step in allowed directions
 */
function generateSteppingMoves(
  state: ChessState,
  piece: Piece,
  offsets: Direction[],
): PossibleMove[] {
  const moves: PossibleMove[] = [];
  const { fileIndex, rankIndex } = squareToIndices(piece.position);

  for (const offset of offsets) {
    const fi = fileIndex + offset.fileOffset;
    const ri = rankIndex + offset.rankOffset;

    if (!isInBounds(fi, ri)) continue;

    const targetSquare = indicesToSquare(fi, ri);
    if (!targetSquare) continue;

    const targetPiece = getPieceAt(state, targetSquare);

    if (targetPiece === null) {
      moves.push({
        from: piece.position,
        to: targetSquare,
        isCapture: false,
      });
    } else if (targetPiece.color !== piece.color) {
      moves.push({
        from: piece.position,
        to: targetSquare,
        isCapture: true,
        capturedPieceId: targetPiece.id,
      });
    }
  }

  return moves;
}

/** Generate pawn moves (forward moves, diagonal captures, en passant, promotion) */
function generatePawnMoves(state: ChessState, piece: Piece): PossibleMove[] {
  const moves: PossibleMove[] = [];
  const { fileIndex, rankIndex } = squareToIndices(piece.position);
  const direction = piece.color === "WHITE" ? 1 : -1;
  const startRank = piece.color === "WHITE" ? 1 : 6; // 0-indexed
  const promotionRankIndices = getPromotionRankIndices(state, piece);

  // PN-010 Last Pawn: if this pawn is the last ally pawn and has LAST_PAWN_MOVEMENT,
  // it can always double-advance from any rank (path must be clear).
  const lastPawnBuff =
    hasLastPawnMovement(state, piece.color) && isLastPawn(state, piece);

  // Forward move (one square)
  const forwardRi = rankIndex + direction;
  if (isInBounds(fileIndex, forwardRi)) {
    const forwardSquare = indicesToSquare(fileIndex, forwardRi);
    if (forwardSquare && !getPieceAt(state, forwardSquare)) {
      const isPromotion = promotionRankIndices.includes(rankIndex);
      moves.push({
        from: piece.position,
        to: forwardSquare,
        isCapture: false,
        moveType: isPromotion ? "PROMOTION" : "NORMAL",
        isPromotion,
      });

      // Double move from starting position, or from any rank when last pawn buff is active
      if (rankIndex === startRank || lastPawnBuff) {
        const doubleRi = rankIndex + direction * 2;
        const doubleSquare = indicesToSquare(fileIndex, doubleRi);
        if (doubleSquare && !getPieceAt(state, doubleSquare)) {
          moves.push({
            from: piece.position,
            to: doubleSquare,
            isCapture: false,
            moveType: "NORMAL",
          });
        }
      }
    }
  }

  // Diagonal captures
  for (const fileOffset of [-1, 1]) {
    const captureFi = fileIndex + fileOffset;
    const captureRi = rankIndex + direction;

    if (!isInBounds(captureFi, captureRi)) continue;

    const captureSquare = indicesToSquare(captureFi, captureRi);
    if (!captureSquare) continue;

    const targetPiece = getPieceAt(state, captureSquare);
    if (targetPiece && targetPiece.color !== piece.color) {
      const isPromotion = promotionRankIndices.includes(rankIndex);
      moves.push({
        from: piece.position,
        to: captureSquare,
        isCapture: true,
        capturedPieceId: targetPiece.id,
        moveType: isPromotion ? "PROMOTION" : "CAPTURE",
        isPromotion,
      });
    }

    // En passant capture
    if (state.enPassantTarget) {
      if (
        captureSquare.file === state.enPassantTarget.file &&
        captureSquare.rank === state.enPassantTarget.rank
      ) {
        // Find the pawn to capture (it's on the same rank as our pawn, adjacent file)
        const capturedPawnSquare: Square = {
          file: state.enPassantTarget.file,
          rank: piece.position.rank,
        };
        const capturedPawn = getPieceAt(state, capturedPawnSquare);
        if (capturedPawn && capturedPawn.type === "PAWN") {
          moves.push({
            from: piece.position,
            to: captureSquare,
            isCapture: true,
            capturedPieceId: capturedPawn.id,
            moveType: "EN_PASSANT",
            enPassantCaptureSquare: capturedPawnSquare,
          });
        }
      }
    }
  }

  return moves;
}

/**
 * Generate castling moves for the king
 */
function generateCastlingMoves(state: ChessState, king: Piece): PossibleMove[] {
  const moves: PossibleMove[] = [];
  const color = king.color;
  const opponentColor = getOppositeColor(color);

  // Cannot castle if king has moved
  if (king.hasMoved) return moves;

  // Cannot castle while in check
  if (isSquareAttackedForCastling(state, king.position, opponentColor)) {
    return moves;
  }

  const rank = color === "WHITE" ? 1 : 8;
  const { castlingRights } = state;

  // Kingside castling
  const canKingside =
    color === "WHITE"
      ? castlingRights.whiteKingside
      : castlingRights.blackKingside;

  if (canKingside) {
    const fSquare: Square = { file: "f", rank };
    const gSquare: Square = { file: "g", rank };
    const hSquare: Square = { file: "h", rank };

    // Check if squares between king and rook are empty
    const fEmpty = !getPieceAt(state, fSquare);
    const gEmpty = !getPieceAt(state, gSquare);

    // Check if rook exists and hasn't moved
    const rook = getPieceAt(state, hSquare);
    const rookValid = rook && rook.type === "ROOK" && !rook.hasMoved;

    // Check if king doesn't pass through or land on attacked squares
    const fSafe = !isSquareAttackedForCastling(state, fSquare, opponentColor);
    const gSafe = !isSquareAttackedForCastling(state, gSquare, opponentColor);

    if (fEmpty && gEmpty && rookValid && fSafe && gSafe) {
      moves.push({
        from: king.position,
        to: gSquare,
        isCapture: false,
        moveType: "CASTLE_KINGSIDE",
      });
    }
  }

  // Queenside castling
  const canQueenside =
    color === "WHITE"
      ? castlingRights.whiteQueenside
      : castlingRights.blackQueenside;

  if (canQueenside) {
    const dSquare: Square = { file: "d", rank };
    const cSquare: Square = { file: "c", rank };
    const bSquare: Square = { file: "b", rank };
    const aSquare: Square = { file: "a", rank };

    // Check if squares between king and rook are empty
    const dEmpty = !getPieceAt(state, dSquare);
    const cEmpty = !getPieceAt(state, cSquare);
    const bEmpty = !getPieceAt(state, bSquare);

    // Check if rook exists and hasn't moved
    const rook = getPieceAt(state, aSquare);
    const rookValid = rook && rook.type === "ROOK" && !rook.hasMoved;

    // Check if king doesn't pass through or land on attacked squares
    const dSafe = !isSquareAttackedForCastling(state, dSquare, opponentColor);
    const cSafe = !isSquareAttackedForCastling(state, cSquare, opponentColor);

    if (dEmpty && cEmpty && bEmpty && rookValid && dSafe && cSafe) {
      moves.push({
        from: king.position,
        to: cSquare,
        isCapture: false,
        moveType: "CASTLE_QUEENSIDE",
      });
    }
  }

  return moves;
}

/**
 * Check if a square is attacked (for castling check - avoids recursion)
 */
function isSquareAttackedForCastling(
  state: ChessState,
  square: Square,
  byColor: Color,
): boolean {
  const attackingPieces = state.pieces.filter((p) => p.color === byColor);

  for (const piece of attackingPieces) {
    // Phase-movement pieces cannot give check, so they cannot attack castling transit squares.
    if (hasPhaseMovement(state, piece)) continue;

    // For castling, we only check basic moves (not castling itself)
    let moves: PossibleMove[];

    if (piece.type === "PAWN") {
      moves = generatePawnMoves(state, piece);
    } else if (piece.type === "KING") {
      moves = generateSteppingMoves(state, piece, ALL_DIRECTIONS);
    } else {
      const effectiveType = getEffectiveMovementType(state, piece);
      const phaseOpts = undefined;
      switch (effectiveType) {
        case "KNIGHT":
          moves = generateSteppingMoves(state, piece, KNIGHT_OFFSETS);
          break;
        case "BISHOP":
          moves = generateSlidingMoves(
            state,
            piece,
            BISHOP_DIRECTIONS,
            phaseOpts,
          );
          break;
        case "ROOK": {
          const castlingRookDom = hasRookLineCapture(state, piece);
          const castlingRookOpts = castlingRookDom
            ? { dominationRook: true }
            : phaseOpts;
          moves = generateSlidingMoves(
            state,
            piece,
            ROOK_DIRECTIONS,
            castlingRookOpts,
          );
          break;
        }
        case "QUEEN": {
          const castlingQueenMaxDist = hasReverseMovement(state, piece)
            ? 3
            : undefined;
          const castlingQueenOpts =
            castlingQueenMaxDist !== undefined
              ? { maxDistance: castlingQueenMaxDist }
              : phaseOpts;
          moves = generateSlidingMoves(
            state,
            piece,
            ALL_DIRECTIONS,
            castlingQueenOpts,
          );
          break;
        }
        default:
          moves = [];
      }
    }
    if (
      moves.some((m) => m.to.file === square.file && m.to.rank === square.rank)
    ) {
      return true;
    }
  }

  return false;
}

/**
 * Generate all pseudo-legal moves for a piece
 * (doesn't check if king is left in check)
 * Includes augmentation-enhanced moves
 */
export function generatePseudoLegalMoves(
  state: ChessState,
  piece: Piece,
): PossibleMove[] {
  let baseMoves: PossibleMove[];

  if (piece.type === "PAWN") {
    baseMoves = generatePawnMoves(state, piece);
  } else if (piece.type === "KING") {
    const normalMoves = generateSteppingMoves(state, piece, ALL_DIRECTIONS);
    const castlingMoves = generateCastlingMoves(state, piece);
    baseMoves = [...normalMoves, ...castlingMoves];
  } else {
    const effectiveType = getEffectiveMovementType(state, piece);
    const phaseOpts = hasPhaseMovement(state, piece)
      ? { phase: true }
      : undefined;
    switch (effectiveType) {
      case "KNIGHT":
        baseMoves = generateSteppingMoves(state, piece, KNIGHT_OFFSETS);
        break;
      case "BISHOP":
        baseMoves = generateSlidingMoves(
          state,
          piece,
          BISHOP_DIRECTIONS,
          phaseOpts,
        );
        break;
      case "ROOK": {
        const rookDom = hasRookLineCapture(state, piece);
        const rookOpts = rookDom
          ? { ...phaseOpts, dominationRook: true }
          : phaseOpts;
        baseMoves = generateSlidingMoves(
          state,
          piece,
          ROOK_DIRECTIONS,
          rookOpts,
        );
        break;
      }
      case "QUEEN": {
        const queenMaxDist = hasReverseMovement(state, piece) ? 3 : undefined;
        const queenOpts =
          queenMaxDist !== undefined
            ? { ...phaseOpts, maxDistance: queenMaxDist }
            : phaseOpts;
        baseMoves = generateSlidingMoves(
          state,
          piece,
          ALL_DIRECTIONS,
          queenOpts,
        );
        break;
      }
      default:
        baseMoves = [];
    }
  }

  // Apply augmentation effects to add additional moves
  const allMoves = applyAugmentationEffects(state, piece, baseMoves);

  // Filter out captures of pieces protected by immunity augments.
  // Exceptions:
  //   - King with KING_FURY bypasses all capture immunity.
  //   - Knight with ASSASSIN (KN-004) bypasses immunity when capturing Queens.
  const kingFuryActive = piece.type === "KING" && hasKingFury(state, piece);
  const assassinActive =
    piece.type === "KNIGHT" && hasAssassinAug(state, piece.color);
  const knightWeakened =
    piece.type === "KNIGHT" && isKnightWeakened(state, piece);

  const filteredMoves = allMoves.filter((move) => {
    if (!move.isCapture || !move.capturedPieceId) return true;
    if (kingFuryActive) return true; // King's Fury bypasses immunity
    const targetPiece = state.pieces.find((p) => p.id === move.capturedPieceId);
    if (!targetPiece) return true;
    // KN-004 Assassin: knight can capture queens regardless of immunity
    if (assassinActive && targetPiece.type === "QUEEN") return true;
    return (
      !isKingAdjacentImmune(state, targetPiece) &&
      !isUnmovedImmune(state, targetPiece) &&
      !isShieldWallImmune(state, targetPiece)
    );
  });

  // KN-004 Assassin: weakened knight's non-queen captures cause mutual destruction
  const movesAfterWeakened = knightWeakened
    ? filteredMoves.map((move) => {
        if (!move.isCapture || !move.capturedPieceId) return move;
        const targetPiece = state.pieces.find(
          (p) => p.id === move.capturedPieceId,
        );
        // Queen captures remain normal (no mutual destruction)
        if (!targetPiece || targetPiece.type === "QUEEN") return move;
        return { ...move, isMutualDestruction: true };
      })
    : filteredMoves;

  // Capture probability annotation pass:
  // For each capture move, compute the capture chance and filter out 0% moves.
  // Skip en-route captures (Domination Rook) — deferred to Phase 2.
  const annotatedMoves: PossibleMove[] = [];
  for (const move of movesAfterWeakened) {
    if (!move.isCapture || !move.capturedPieceId) {
      annotatedMoves.push(move);
      continue;
    }

    // Skip annotation for en-route-only captures (no landing capture)
    if (move.capturedEnRoutePieceId && !move.capturedPieceId) {
      annotatedMoves.push(move);
      continue;
    }

    const targetPiece = state.pieces.find((p) => p.id === move.capturedPieceId);
    if (!targetPiece) {
      annotatedMoves.push(move);
      continue;
    }

    const chance = calculateCaptureChance(state, piece, targetPiece);
    if (chance <= 0) continue; // 0% capture = equivalent to immunity, filter out

    if (chance < 1.0) {
      annotatedMoves.push({ ...move, captureChance: chance });
    } else {
      annotatedMoves.push(move);
    }
  }

  return annotatedMoves;
}

/**
 * Simulate a move on the board (for checking legality)
 * Returns a new state with the move applied
 */
function simulateMove(
  state: ChessState,
  piece: Piece,
  move: PossibleMove,
): ChessState {
  // Deep clone state
  const newState = JSON.parse(JSON.stringify(state)) as ChessState;

  // Find and update the piece
  const pieceIndex = newState.pieces.findIndex((p) => p.id === piece.id);
  if (pieceIndex !== -1) {
    const simPiece = newState.pieces[pieceIndex];
    if (simPiece) {
      simPiece.position = move.to;
    }
  }

  // Remove captured piece if any
  if (move.capturedPieceId) {
    newState.pieces = newState.pieces.filter(
      (p) => p.id !== move.capturedPieceId,
    );
  }

  // Remove en-route captured piece (Domination Rook)
  if (move.capturedEnRoutePieceId) {
    newState.pieces = newState.pieces.filter(
      (p) => p.id !== move.capturedEnRoutePieceId,
    );
  }

  // KN-004 Assassin mutual destruction: weakened knight captures non-queen → both removed
  if (move.isMutualDestruction) {
    newState.pieces = newState.pieces.filter((p) => p.id !== piece.id);
  }

  return newState;
}

/**
 * Check if a square is attacked by a specific color
 */
export function isSquareAttacked(
  state: ChessState,
  square: Square,
  byColor: Color,
): boolean {
  const attackingPieces = state.pieces.filter((p) => p.color === byColor);

  for (const piece of attackingPieces) {
    const moves = generatePseudoLegalMoves(state, piece);
    if (
      moves.some((m) => m.to.file === square.file && m.to.rank === square.rank)
    ) {
      return true;
    }
  }

  return false;
}

/**
 * Check if the current player's king is in check
 */
export function isInCheck(state: ChessState, color: Color): boolean {
  const king = getKing(state, color);
  if (!king) return false;

  const opponentColor = getOppositeColor(color);
  return isSquareAttacked(state, king.position, opponentColor);
}

/**
 * Generate legal moves for a piece
 * Filters out moves that would leave the king in check
 */
export function generateLegalMoves(
  state: ChessState,
  piece: Piece,
): PossibleMove[] {
  const pseudoMoves = generatePseudoLegalMoves(state, piece);

  return pseudoMoves.filter((move) => {
    // Simulate the move
    const simulatedState = simulateMove(state, piece, move);

    // Check if own king is in check after the move
    return !isInCheck(simulatedState, piece.color);
  });
}

/**
 * Get all legal moves for a color
 */
export function getAllLegalMoves(
  state: ChessState,
  color: Color,
): Map<string, PossibleMove[]> {
  const movesByPiece = new Map<string, PossibleMove[]>();
  const pieces = state.pieces.filter((p) => p.color === color);

  for (const piece of pieces) {
    const moves = generateLegalMoves(state, piece);
    if (moves.length > 0) {
      movesByPiece.set(piece.id, moves);
    }
  }

  return movesByPiece;
}

/**
 * Check if a color has any legal moves
 */
export function hasLegalMoves(state: ChessState, color: Color): boolean {
  const pieces = state.pieces.filter((p) => p.color === color);

  for (const piece of pieces) {
    const moves = generateLegalMoves(state, piece);
    if (moves.length > 0) {
      return true;
    }
  }

  return false;
}

/**
 * Check if the game is in checkmate for the given color
 * Checkmate = king is in check AND no legal moves available
 */
export function isCheckmate(state: ChessState, color: Color): boolean {
  return isInCheck(state, color) && !hasLegalMoves(state, color);
}

/**
 * Check if the game is in stalemate for the given color
 * Stalemate = king is NOT in check AND no legal moves available
 */
export function isStalemate(state: ChessState, color: Color): boolean {
  return !isInCheck(state, color) && !hasLegalMoves(state, color);
}

/**
 * Check if the game has threefold repetition
 * Returns true if the current position has occurred 3 or more times
 */
export function hasThreefoldRepetition(state: ChessState): boolean {
  if (!state.positionHistory || state.positionHistory.length < 3) {
    return false;
  }

  // Get the current position (last in history)
  const currentPosition =
    state.positionHistory[state.positionHistory.length - 1];

  // Count occurrences
  let count = 0;
  for (const position of state.positionHistory) {
    if (position === currentPosition) {
      count++;
      if (count >= 3) return true;
    }
  }

  return false;
}

/**
 * Get the game result based on current state
 */
export function getGameResult(state: ChessState): {
  ended: boolean;
  result?: "WHITE_WINS" | "BLACK_WINS" | "DRAW";
  reason?: string;
} {
  const currentColor = state.currentPlayer;

  if (isCheckmate(state, currentColor)) {
    const winner = currentColor === "WHITE" ? "BLACK_WINS" : "WHITE_WINS";
    return { ended: true, result: winner, reason: "checkmate" };
  }

  // Consecutive check win (overrides draws/stalemate)
  const consecutiveCheckResult = checkConsecutiveCheckWin(state);
  if (consecutiveCheckResult) {
    return consecutiveCheckResult;
  }

  if (isStalemate(state, currentColor)) {
    return { ended: true, result: "DRAW", reason: "stalemate" };
  }

  // 50-move rule
  if (state.halfMoveClock >= 100) {
    return { ended: true, result: "DRAW", reason: "fifty-move rule" };
  }

  // Threefold repetition
  if (hasThreefoldRepetition(state)) {
    return { ended: true, result: "DRAW", reason: "threefold repetition" };
  }

  return { ended: false };
}
